import type { FC } from "react";
import { useState } from "react";

import styled from "styled-components";

import type { IconName } from "@similarweb/icons";
import { PlainTooltip, Button, IconButton } from "@similarweb/ui-components";

import type { IUserKeywordGroup } from "app/services/keyword-groups/keywordGroups.types";
import { allTrackers } from "app/services/track/track";
import { translate } from "app/filters/i18nFilter";
import { KeywordsGroupEditorModal } from "app/pages/keyword-analysis/KeywordsGroupEditorModal";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { openHooksModalV5 } from "app/services/ModalService";
import type { IAccountUser, IShareUser } from "app/sharing/SharingService";
import { SharingService } from "app/sharing/SharingService";
import { RightFlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { isTrialOrAfterTrial } from "app/UtilitiesAndConstants/UtilityFunctions/trialUsersUtils";

const HeaderContainer = styled(RightFlexRow)`
    column-gap: 8px;
    padding-bottom: 24px;
`;

const transformUsers = (users: IShareUser[]): IAccountUser[] =>
    users.map((user) => ({
        Id: user.id,
        User: `${user.firstName} ${user.lastName}`,
        Email: user.email,
        FirstName: user.firstName,
        LastName: user.lastName,
    }));

interface IPageCTAHeaderProps {
    sharedWithUsers: IAccountUser[];
    sharedWithAccounts: IAccountUser[];
    onShareClick: () => void;
    keywordGroup: IUserKeywordGroup;
    users?: IShareUser[];
}

export const PageCTAHeader: FC<IPageCTAHeaderProps> = ({
    sharedWithUsers,
    sharedWithAccounts,
    onShareClick,
    keywordGroup,
    users,
}) => {
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);

    const onDelete = (deletedGroup: IUserKeywordGroup) => {
        // In case the group we're deleting is the currently displayed
        // group in the query bar - we should navigate back to the homepage
        if (deletedGroup.Id === keywordGroup.Id) {
            const homeState = proNavigator.current().homeState;
            proNavigator.go(homeState);
        }
    };

    const onEditButtonClicked = () => {
        setIsEditModalOpen(true);
        allTrackers.dispatchTrackEvent("Button", "click", `add or edit keywords/${keywordGroup.Name}`);
    };

    return (
        <HeaderContainer>
            {users ? (
                <ShareButtonComponent
                    sharedWithAccounts={sharedWithAccounts}
                    sharedWithUsers={sharedWithUsers}
                    users={users}
                    onShareClick={onShareClick}
                />
            ) : null}
            <Button onClick={onEditButtonClicked}>{translate("monitor.keywords.page.cta.header.add.or.edit")}</Button>
            <KeywordsGroupEditorModal
                onClose={() => setIsEditModalOpen(false)}
                open={isEditModalOpen}
                keywordsGroup={keywordGroup}
                onDelete={onDelete}
                editTargetState="digitalsuite_monitorkeywords"
            />
        </HeaderContainer>
    );
};

const ShareButtonComponent: FC<Omit<IPageCTAHeaderProps, "keywordGroup">> = ({
    sharedWithAccounts,
    sharedWithUsers,
    users,
    onShareClick,
}) => {
    const isGroupShared = sharedWithAccounts?.length > 0 || sharedWithUsers?.length > 0;
    const isTrial = isTrialOrAfterTrial();

    if (isGroupShared) {
        const text = SharingService.getShareTooltip({
            sharedWithAccounts,
            sharedWithUsers,
            users: transformUsers(users),
        });
        const iconName: IconName = sharedWithAccounts ? "company" : "social-share";

        return (
            <PlainTooltip
                tooltipContent={text}
                placement="bottom"
            >
                <span>
                    <IconButton
                        type="outlined"
                        onClick={onShareClick}
                        iconName={iconName}
                        iconSize="xs"
                    >
                        {translate("workspaces.marketing.header.shared")}
                    </IconButton>
                </span>
            </PlainTooltip>
        );
    } else if (isTrial) {
        return (
            <IconButton
                type="outlined"
                onClick={() => openHooksModalV5("GetMoreResults", "keywordGroup/Share")}
                iconName="locked"
                iconSize="xs"
            >
                {translate("workspaces.marketing.header.share")}
            </IconButton>
        );
    } else {
        return (
            <IconButton
                type="outlined"
                onClick={onShareClick}
                iconName="social-share"
                iconSize="xs"
            >
                {translate("workspaces.marketing.header.share")}
            </IconButton>
        );
    }
};
